import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import toast from '../../shared/Toast';
import { hapticError, hapticSuccess, hapticTick } from '../../../_utils/haptics';
import { checkCode, clientKeyOf, useDiscountCodes, consentFrom, loadChoices } from '../discountStore';
import { cleanCode, headlineOf, looksLikeGiftCard, quote, stateOf, termsOf, valueSaid } from '../data/codes';
import { lineDiscountsFrom, loadCategories, loadSelfEmployed, skippedForConsent, tillLines } from './tillCode';
import './till.css';

/**
 * Add a code, at diary checkout and the till. Anyone who can take payment
 * can apply a code a client shows them: the code's own rules decide the
 * discount, so it isn't a discretionary one (that's Discount). One discount
 * per sale: a code replaces a manual discount, and the other way round.
 *
 *   items       the sale's lines ({ type, commonServiceId, staffId, staffName, originalPrice, quantity })
 *   promo       the code already on the sale, if any
 *   client      { phoneNumber, _id } for one use each
 *   onApply(promo, lineDiscounts) / onRemove() / onBack()
 */
export default function CodeScreen({ items, promo, client, salonId, subtotal, currencySymbol = '£', onApply, onRemove, onBack }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const input = useRef(null);
  const fmt = (n) => `${Number(n) < 0 ? '−' : ''}${currencySymbol}${Math.abs(Number(n) || 0).toFixed(2)}`;

  // The live codes that fit this sale, for a client who saw an offer but
  // doesn't have the code to hand. Worked out here from the codes' own rules;
  // tapping one still goes through the full check (one use each and all).
  const { codes, loaded } = useDiscountCodes();
  const [fits, setFits] = useState(null);
  useEffect(() => {
    if (promo || !loaded) return undefined;
    let alive = true;
    Promise.all([loadCategories(), loadSelfEmployed(), loadChoices().catch(() => ({}))]).then(([categories, seIds, choices]) => {
      if (!alive) return;
      const lines = tillLines(items, categories);
      const day = moment().format('YYYY-MM-DD');
      const list = (codes || [])
        .filter((c) => stateOf(c) === 'live' && (c.channels || {}).salon !== false)
        .map((c) => ({ c, q: quote(c, { lines, date: day, channel: 'salon', client: {}, consentOf: consentFrom(choices, seIds) }) }))
        .filter((x) => x.q.ok && x.q.amount > 0)
        .sort((a, b) => b.q.amount - a.q.amount);
      setFits(list);
    }).catch(() => { if (alive) setFits([]); });
    return () => { alive = false; };
  }, [promo, loaded, codes, items]);

  const apply = async (e, picked) => {
    if (e) e.preventDefault();
    const typed = cleanCode(picked || text);
    if (!typed) { setError('Type the client’s code first.'); hapticError(); return; }
    if (looksLikeGiftCard(text)) { setError('That’s a gift card code. Take it as a gift card payment instead.'); hapticError(); return; }
    setBusy(true);
    setError('');
    try {
      const [categories, selfEmployedIds] = await Promise.all([loadCategories(), loadSelfEmployed()]);
      const lines = tillLines(items, categories);
      const r = await checkCode({
        salonId: salonId || localStorage.getItem('salonId'),
        typed,
        lines,
        date: moment().format('YYYY-MM-DD'),
        channel: 'salon',
        client: { key: clientKeyOf({ phone: client && (client.phoneNumber || client.phone), userId: client && client._id }) },
        selfEmployedIds,
      });
      if (!r.ok) {
        // The till can say more than the booking page: who hasn't agreed
        const who = r.reason === 'no_lines' ? skippedForConsent(r, items) : [];
        setError(who.length ? `${r.message} ${who.join(' and ')} ${who.length === 1 ? 'hasn’t' : 'haven’t'} said yes to the owner’s codes on their services.` : (r.message || 'That code can’t be used on this sale.'));
        hapticError();
        setBusy(false);
        return;
      }
      const code = r.code || { code: typed, type: 'fixed', value: r.amount };
      const lineDiscounts = lineDiscountsFrom(code, r, items);
      const next = {
        code: code.code,
        codeId: code.id || null,
        type: code.type,
        value: code.value,
        amount: r.amount,
        said: valueSaid(code),
        terms: termsOf(code),
        serverChecked: r.source === 'server',
        lines: (r.lines || []).filter((l) => l.eligible && l.amount > 0).map((l) => ({ index: Number(String(l.id).replace(/^l/, '')), amount: l.amount })),
      };
      onApply(next, lineDiscounts);
      hapticSuccess();
      const skipped = skippedForConsent(r, items);
      toast.success(`${code.code} applied, ${fmt(r.amount)} off`);
      if (skipped.length) toast.info(`Not on ${skipped.join(' and ')}’s services: they haven’t said yes to the owner’s codes.`);
      onBack();
    } catch (err) {
      setError('Codes can’t be checked just now. Try again in a moment.');
      hapticError();
      setBusy(false);
    }
  };

  return (
    <div className="co-discount dct">
      <div className="co-screen-header">
        <button className="co-back-btn" onClick={onBack} aria-label="Back">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
        </button>
        <h2 className="co-screen-title">Discount code</h2>
      </div>

      <div className="co-discount-body dct-body">
        {promo ? (
          <div className="dct-applied" role="status">
            <span className="dct-code">{promo.code}</span>
            <div className="dct-applied-text">
              <b>{promo.said} · {fmt(-promo.amount)}</b>
              {promo.terms && <span>{promo.terms}</span>}
            </div>
          </div>
        ) : (
          <form onSubmit={apply} noValidate className="dct-form">
            <label className="dct-label" htmlFor="dct-input">The client’s code</label>
            <input
              ref={input}
              id="dct-input"
              className={`dct-input${error ? ' is-bad' : ''}`}
              value={text}
              autoCapitalize="characters"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              placeholder="For example, AUTUMN20"
              aria-invalid={!!error}
              aria-describedby={error ? 'dct-error' : 'dct-help'}
              onChange={(e) => { const v = e.target.value; setText(v.toUpperCase()); if (error) setError(''); }}
            />
            {error
              ? <p className="dct-error" id="dct-error" role="alert">{error}</p>
              : <p className="dct-help" id="dct-help">Its rules decide what comes off, so a code replaces any manual discount. Tips and deposits are never discounted.</p>}
          </form>
        )}

        {!promo && fits && (
          <section className="dct-fits" aria-label="Codes that fit this sale">
            <h3 className="dct-label">{fits.length ? 'Codes that fit this sale' : 'No live codes fit this sale'}</h3>
            {fits.length > 0 && (
              <div className="dct-fit-list">
                {fits.map(({ c, q }) => (
                  <button key={c.id} type="button" className="dct-fit" disabled={busy} onClick={() => { hapticTick(); setText(c.code); apply(null, c.code); }}>
                    <span className="dct-code">{c.code}</span>
                    <span className="dct-fit-text">
                      <b>{headlineOf(c)}</b>
                      <span>{[c.audience === 'new' && 'New clients only', c.endsOn && `Book by ${moment(c.endsOn, 'YYYY-MM-DD').format('ddd D MMM')}`].filter(Boolean).join(' · ') || 'No end date'}</span>
                    </span>
                    <span className="dct-fit-amount">{fmt(-q.amount)}</span>
                  </button>
                ))}
              </div>
            )}
          </section>
        )}

        <div className="co-discount-preview">
          <div className="co-discount-preview-row"><span>Subtotal</span><span>{fmt(subtotal)}</span></div>
          {promo && <div className="co-discount-preview-row co-discount-preview-row--savings"><span>{promo.code}</span><span>{fmt(-promo.amount)}</span></div>}
          <div className="co-discount-preview-row co-discount-preview-row--total"><span>{promo ? 'New total' : 'Total'}</span><span>{fmt(Math.max(0, subtotal - (promo ? promo.amount : 0)))}</span></div>
        </div>
      </div>

      <div className="co-pl-footer">
        {promo ? (
          <>
            <button className="bdp-btn bdp-btn--secondary" style={{ color: '#ef4444' }} onClick={() => { hapticTick(); onRemove(); onBack(); }}>Remove code</button>
            <button className="bdp-btn bdp-btn--primary" onClick={onBack}>Done</button>
          </>
        ) : (
          <>
            <button className="bdp-btn bdp-btn--secondary" onClick={onBack}>Cancel</button>
            <button className="bdp-btn bdp-btn--primary" onClick={apply} disabled={busy || !text.trim()}>{busy ? 'Checking…' : 'Apply code'}</button>
          </>
        )}
      </div>
    </div>
  );
}
